import { DuplicateIcon, LockClosedIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { FaShopify } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * The Shopify connection window, connected, with the chat code ready to
 * paste, and an order lookup that only answers once the details match.
 */
export default function ShopifyConnect({ brand }: { brand: string }) {
  const { t } = useLocale('bot-use-cases');

  return (
    <div className='relative pb-0 sm:pb-24'>
      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
          <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#f3f3f7] dark:bg-white'>
            <FaShopify className='h-5 w-5 text-[#95BF47]' />
          </span>
          <p className={clsxm('flex-1 text-[15px] font-semibold', tone.ink)}>
            Shopify
          </p>
          <span className='flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
            <CheckGlyph className='h-3.5 w-3.5' />
            {t('Connected')}
          </span>
        </div>

        <dl className='space-y-4 px-5 py-5 text-[13px]'>
          <div>
            <dt className={tone.quiet}>{t('Shop domain')}</dt>
            <dd
              className={clsxm(
                'mt-1.5 rounded-xl bg-[#f7f7fa] px-3.5 py-2.5 font-medium dark:bg-white/[0.04]',
                tone.ink,
              )}
              dir='ltr'
            >
              fernwood-boots.myshopify.com
            </dd>
          </div>
          <div>
            <dt className={tone.quiet}>{t('Access')}</dt>
            <dd className='mt-1.5 flex flex-wrap gap-2'>
              {[t('Read products'), t('Read orders')].map((scope) => (
                <span
                  key={scope}
                  className={clsxm(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-medium',
                    tone.line,
                    tone.ink,
                  )}
                >
                  <CheckGlyph className='h-3.5 w-3.5 text-emerald-500' />
                  {scope}
                </span>
              ))}
            </dd>
          </div>
          <div>
            <dt className={tone.quiet}>{t('Chat widget embed code')}</dt>
            <dd className='mt-1.5 flex items-start gap-3 rounded-xl bg-[#0d0c1f] px-3.5 py-3 font-mono text-[12px] leading-5 text-[#c9c9e6]'>
              <code className='min-w-0 flex-1 break-all' dir='ltr'>
                <span className='text-[#8f8fff]'>&lt;script</span>{' '}
                <span className='text-[#7dd3c0]'>src</span>
                =&quot;…/chat.min.js&quot;{' '}
                <span className='text-[#7dd3c0]'>defer</span>
                <span className='text-[#8f8fff]'>&gt;&lt;/script&gt;</span>
              </code>
              <DuplicateIcon className='mt-0.5 h-4 w-4 flex-none text-white/50' />
            </dd>
          </div>
        </dl>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'mt-4 rounded-2xl p-4 shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:absolute sm:bottom-0 sm:end-6 sm:mt-0 sm:w-[18.5rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(600)}
      >
        <p className='flex items-center justify-between gap-3'>
          <span className={clsxm('text-[13px] font-semibold', tone.ink)}>
            {t('Order')} #1093
          </span>
          <span
            className={clsxm(
              'rounded-full px-2 py-0.5 text-[11px] font-semibold text-white',
              brand,
            )}
          >
            {t('Out for delivery')}
          </span>
        </p>
        <p
          className={clsxm(
            'mt-2.5 flex items-center gap-1.5 text-xs',
            tone.quiet,
          )}
        >
          <LockClosedIcon className='h-3.5 w-3.5 flex-none' />
          {t('Shared after the order number and email matched')}
        </p>
      </div>
    </div>
  );
}
